import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../components/landing/landing.css";

import { LandingNavbar }         from "../components/landing/LandingNavbar";
import { LandingHero }           from "../components/landing/LandingHero";
import { LandingTrustSection }   from "../components/landing/LandingTrustSection";
import { LandingDeviceShowcase } from "../components/landing/LandingDeviceShowcase";
import { LandingMapSection }     from "../components/landing/LandingMapSection";
import { LandingBusinessCTA }    from "../components/landing/LandingBusinessCTA";
import { LandingAppDownload }    from "../components/landing/LandingAppDownload";
import { LandingAboutSection }   from "../components/landing/LandingAboutSection";
import { LandingFAQSection }     from "../components/landing/LandingFAQSection";
import { LandingPageFooter }     from "../components/landing/LandingPageFooter";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;1,9..144,600&family=Outfit:wght@300;400;500&display=swap";

const APP_TYPE = process.env.REACT_APP_APP_TYPE || "dev";

function getAuthRedirect(user) {
  if (APP_TYPE === "admin") {
    const has = (p) => user.permissions && user.permissions.includes(p);
    if (user.role === "admin" || has("admin_access")) return "/control-tower";
    if (has("content")) return "/categories";
    if (has("users")) return "/user-management";
    if (has("venues")) return "/venue-verification";
    return `/view-profile/${user.id}`;
  }
  if (APP_TYPE === "business") return "/applications";

  // dev / default
  if (user.role === "admin" || (user.permissions && user.permissions.includes("admin_access"))) {
    return "/control-tower";
  }
  if (user.permissions && user.permissions.includes("dashboard")) return "/index-1";
  return "/applications";
}

export default function LandingPage() {
  const { user, isAuthenticated } = useAuth();
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [theme, setTheme] = useState("light");

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  // Monitor scroll for sticky bar visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling down 500px
      setShowStickyBar(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Inject Google Fonts and SEO attributes on mount
  useEffect(() => {
    // 1. Google Fonts
    const existingFonts = document.getElementById("hs-landing-fonts");
    if (!existingFonts) {
      const link = document.createElement("link");
      link.id   = "hs-landing-fonts";
      link.rel  = "stylesheet";
      link.href = FONTS_HREF;
      document.head.appendChild(link);
    }

    // 2. SEO Title
    const originalTitle = document.title;
    document.title = "Hubscroll - Discover the UAE's Best Venues";

    // 3. SEO Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    let originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "Hubscroll is the easiest way to discover the most exciting cafés, restaurants, lounges, and nightlife venues across Dubai and the UAE."
    );

    // Clean up on unmount
    return () => {
      const el = document.getElementById("hs-landing-fonts");
      if (el) el.remove();
      
      document.title = originalTitle;
      if (metaDesc) {
        if (originalDesc) {
          metaDesc.setAttribute("content", originalDesc);
        } else {
          metaDesc.remove();
        }
      }
    };
  }, []);

  // Authenticated users skip the landing page
  if (isAuthenticated && user) {
    return <Navigate to={getAuthRedirect(user)} replace />;
  }

  return (
    <div className={`hs-landing ${theme === "light" ? "lp-light-theme" : ""}`}>
      {/* 1. Navigation Bar */}
      <LandingNavbar theme={theme} toggleTheme={toggleTheme} />
      
      {/* 2. Hero Section */}
      <LandingHero />
      
      {/* 3. Trust / Social Proof */}
      <LandingTrustSection />
      
      {/* 4 & 5. Discovery Experience & Key Features */}
      <LandingDeviceShowcase />
      
      {/* 6. UAE Focus Map */}
      <LandingMapSection />
      
      {/* 7. Business Section */}
      <LandingBusinessCTA />
      
      {/* 8. App Download Section */}
      <LandingAppDownload />
      
      {/* 9. About Hubscroll */}
      <LandingAboutSection />
      
      {/* 10. FAQ Section */}
      <LandingFAQSection />
      
      {/* 11. Footer */}
      <LandingPageFooter />

      {/* Extra: Sticky Mobile Download Bar */}
      <div className={`lp-sticky-bar ${showStickyBar ? "lp-active" : ""}`}>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: "0.95rem" }} className="lp-text-dark">
          hub<span className="lp-text-blue">scroll</span>
        </span>
        <div className="lp-flex lp-gap-2">
          <a 
            href="#download"
            className="lp-text-xs lp-px-3 lp-py-15 lp-r-full lp-bg-white lp-text-dark lp-font-medium lp-border"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            iOS
          </a>
          <a 
            href="#download"
            className="lp-text-xs lp-px-3 lp-py-15 lp-r-full lp-border lp-bc-b15 lp-text-dark"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Android
          </a>
        </div>
      </div>

      {/* Extra: Floating Business Button */}
      <Link to="/sign-in" className="lp-floating-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
        List Your Venue
      </Link>
    </div>
  );
}
